/**
 * Trunnion AI - Workflow Designer Page
 * /workflows/:id/edit and /workflows/new
 */

import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { WorkflowDesigner } from '@/components/designer/WorkflowDesigner';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useWorkflow, useCreateWorkflow, useUpdateWorkflow, useExecuteWorkflow } from '@/hooks/queries/useWorkflows';
import { useWorkflowDesignerStore } from '@/stores/workflowDesignerStore';
import { ROUTES } from '@/config/routes';
import type { ClassificationLevel, WorkflowNodeData, WorkflowEdgeData } from '@/types';
import type { Node, Edge } from '@xyflow/react';

export default function WorkflowDesignerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const { data: workflow, isLoading } = useWorkflow(id ?? '');
  const createWorkflow = useCreateWorkflow();
  const updateWorkflow = useUpdateWorkflow();
  const executeWorkflow = useExecuteWorkflow();

  const { loadWorkflow, resetDesigner, nodes, edges, viewport, markClean } =
    useWorkflowDesignerStore();

  // Load workflow data into store
  useEffect(() => {
    if (isNew) {
      resetDesigner();
    } else if (workflow) {
      loadWorkflow({
        id: workflow.id,
        name: workflow.name,
        description: workflow.description,
        classification: workflow.classification,
        version: workflow.version,
        nodes: workflow.nodes as unknown as Node<WorkflowNodeData>[],
        edges: workflow.edges as unknown as Edge<WorkflowEdgeData>[],
        viewport: workflow.viewport,
      });
    }
  }, [workflow, isNew, loadWorkflow, resetDesigner]);

  const handleSave = async (meta: {
    name: string;
    description: string;
    classification: string;
  }) => {
    const workflowData = {
      name: meta.name,
      description: meta.description,
      classification: meta.classification as ClassificationLevel,
      nodes: nodes.map((n) => ({
        id: n.id,
        type: n.type!,
        position: n.position,
        data: n.data,
      })),
      edges: edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle ?? undefined,
        targetHandle: e.targetHandle ?? undefined,
        type: e.type ?? 'dataFlow',
        label: typeof e.label === 'string' ? e.label : undefined,
        data: e.data,
      })),
      tags: [],
      metadata: { viewport },
    };

    if (isNew) {
      createWorkflow.mutate(workflowData as Parameters<typeof createWorkflow.mutate>[0], {
        onSuccess: (wf) => {
          markClean();
          navigate(ROUTES.WORKFLOW_EDIT(wf.id), { replace: true });
        },
      });
    } else if (id) {
      updateWorkflow.mutate(
        { id, data: workflowData as Parameters<typeof updateWorkflow.mutate>[0]['data'] },
        { onSuccess: () => markClean() },
      );
    }
  };

  const handleExecute = () => {
    if (!id || isNew) return;
    executeWorkflow.mutate(
      { id },
      {
        onSuccess: (data) =>
          navigate(ROUTES.EXECUTION_DETAIL(data.executionId)),
      },
    );
  };

  if (!isNew && isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="lg" label="Loading workflow..." />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-theme(spacing.14)-50px)]">
      <WorkflowDesigner
        onSave={handleSave}
        onExecute={!isNew ? handleExecute : undefined}
        saving={createWorkflow.isPending || updateWorkflow.isPending}
      />
    </div>
  );
}
